<template>
  <div class="manage-subscription mb-5 mx-3 mt-3">
    <h1 class="mb-3">{{ isEdit ? 'Edit' : 'Add' }} Subscription</h1>

    <div class="card">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-6 col-12">
            <label class="form-label">Nonprofit Instance <span class="text-muted">(Optional)</span></label>
            <accordion-select :options="clientOptionsFormatted" :splitText="false" v-model="form.client_id" @update:modelValue="clientChanged" :disabled="isViewMode" />
          </div>
          <div class="col-md-6 col-12">
            <label class="form-label">Paid By (Client / Agency)</label>
            <accordion-select :options="paidByOptionsFormatted" :splitText="false" v-model="form.paid_by" :class="{'is-invalid': errors.paid_by}" @update:modelValue="clearPaidByError" :disabled="isViewMode" />
            <div v-if="errors.paid_by" class="invalid-feedback">{{ errors.paid_by }}</div>
          </div>
        </div>

        <div class="row mb-3" v-if="!isEdit">
          <div class="col-md-12 col-12">
            <label class="form-label">HubSpot Deal</label>
            <accordion-select :options="dealOptionsFormatted" :splitText="false"  v-model="form.hubspot_deal_id" :disabled="isViewMode" />
          </div>
        </div>


        <div class="row mb-3">
          <div class="col-md-6 col-12">
            <label class="form-label">Referral Agency (Only select if the agency is getting a referral payment)</label>
            <accordion-select :options="agencyOptionsFormatted"  :splitText="false" v-model="form.referral_agency_id" :disabled="isViewMode" />
          </div>
          <div class="col-md-6 col-12">
            <label class="form-label">Proposal Signed At</label>
            <DatepickerUtc 
              v-model="form.proposal_signed_at"
              :disabled="isViewMode"
              input-format="MM/dd/yyyy"
              input-class="form-control"
              :class="{ 'is-invalid': errors.proposal_signed_at }"
            />
            <div v-if="errors.proposal_signed_at" class="invalid-feedback">{{ errors.proposal_signed_at }}</div>
          </div>
        </div>

        <!-- General Line Items Error -->
        <div v-if="errors.line_items" class="alert alert-danger mb-3">{{ errors.line_items }}</div>

        <!-- Line Items Table -->
        <div class="card table-card mb-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Line Items</h4>
            <button v-if="!isViewMode" class="btn btn-secondary" @click.prevent="addLineItem"><i class="fa fa-plus me-1"></i>Add Item</button>
          </div>
          <div class="card-body p-0">
            <table class="table mb-0">
              <thead>
                <tr>
                  <th></th>
                  <th style="width:250px">Item</th>
                  <th style="width:190px">Discount</th>
                  <th>Frequency</th>
                  <th style="width:170px">Term</th>
                  <th>Start</th>
                  <th>End</th>
                  <th style="width:100px">Qty</th>
                  <th v-if="showReferralEligible" class="text-center">
                    Eligible
                    <img class="info-icon" src="/img/icons/avid-icons/info.svg"
                       data-bs-toggle="tooltip"
                       title="Should the referring agency be paid for this line item?" alt=""></img>
                  </th>
                  <th width="1">Total</th>
                  <th width="1">Net</th>
                  <th width="1">Tax</th>
                  <th></th>
                </tr>
              </thead>
              <draggable tag="tbody" v-model="form.line_items" handle=".drag-handle" item-key="order" :disabled="isViewMode">
                <template #item="{element, index}">
                  <tr class="align-middle">
                    <td class="drag-handle align-middle"><i class="fas fa-grip-vertical"></i></td>
                    <td class="align-middle">
                      <accordion-select :options="catalogOptionsFormatted" :splitText="false" v-model="element.stripe_catalog_id" @update:modelValue="catalogChanged(index); clearCatalogError(index)" :class="{'is-invalid': errors[`catalog_${index}`]}" :disabled="isViewMode" />
                      <div v-if="errors[`catalog_${index}`]" class="invalid-feedback d-block">{{ errors[`catalog_${index}`] }}</div>
                    </td>
                    <td class="align-middle">
                      <accordion-select :options="couponOptionsFormatted" :splitText="false"  v-model="element.stripe_coupon_id" @update:modelValue="couponChanged(index)" :disabled="isViewMode" />
                    </td>
                    <td class="align-middle" :colspan="element.billing_frequency === 'once' ? 2 : 1">
                      <select class="form-select" v-model="element.billing_frequency" @change="frequencyChanged(index)" :disabled="isViewMode" aria-label="Billing Frequency">
                        <option value="once">Once</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </td>
                    <td v-if="element.billing_frequency !== 'once'" class="align-middle">
                      <input type="number" min="1" class="form-control d-inline-block" style="width:60px;" v-model.number="element.term" @input="recalc(index)" :class="{'is-invalid': errors[`term_${index}`]}" :disabled="true" aria-label="Number">
                      <small class="text-muted ms-1">{{ termUnit(element.billing_frequency) }}</small>
                      <div v-if="errors[`term_${index}`]" class="invalid-feedback d-block">{{ errors[`term_${index}`] }}</div>
                    </td>
                    <td class="align-middle">
                      <DatepickerUtc v-model="element.start_date" @update:modelValue="recalc(index); clearStartDateError(index)" :class="{'is-invalid': errors[`li_start_${index}`]}" :disabled="isViewMode" />
                      <div v-if="errors[`li_start_${index}`]" class="invalid-feedback d-block">{{ errors[`li_start_${index}`] }}</div>
                    </td>
                    <td class="align-middle">
                      <DatepickerUtc v-model="element.end_date" @update:modelValue="recalc(index); clearEndDateError(index)" :class="{'is-invalid': errors[`li_end_${index}`]}" :disabled="isViewMode" />
                      <div v-if="errors[`li_end_${index}`]" class="invalid-feedback d-block">{{ errors[`li_end_${index}`] }}</div>
                    </td>
                    <td class="align-middle">
                      <input type="number" min="1" class="form-control" v-model.number="element.quantity" @input="recalc(index); clearQuantityError(index)" :class="{'is-invalid': errors[`qty_${index}`]}" :disabled="isViewMode" aria-label="Number">
                      <div v-if="errors[`qty_${index}`]" class="invalid-feedback d-block">{{ errors[`qty_${index}`] }}</div>
                    </td>
                    <td v-if="showReferralEligible" class="align-middle text-center">
                      <input type="checkbox" class="form-check-input" v-model="element.referral_eligible" :disabled="isViewMode" aria-label="Referral Eligible">
                    </td>
                    <td class="align-middle">{{ currency_with_zero(element.total_amount) }}</td>
                    <td class="align-middle">{{ currency_with_zero(element.net_amount) }}</td>
                    <td class="align-middle">{{ currency_with_zero(element.tax_amount) }}</td>
                    <td class="align-middle text-nowrap">
                        <button v-if="!isViewMode" class="btn btn-none btn-sm bs-tooltip" :class="{'text-danger': element.description}" @click="openDescriptionModal(index)" :title="element.description || 'Add description'">
                            <img class="icon" src="/img/icons/avid-icons/edit.svg" alt="">  
                        </button>
                        <button v-if="!isViewMode" class="btn btn-none btn-sm" @click.prevent="removeLineItem(index)"><img class="icon red-icon" src="/img/icons/avid-icons/trash.svg" alt=""></button>
                    </td>
                  </tr>
                </template>
              </draggable>
            </table>
          </div>
        </div>

        <!-- Description Modal -->
        <div class="modal fade" id="descriptionModal" tabindex="-1" aria-labelledby="descriptionModalLabel" aria-hidden="true">
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h4 class="modal-title" id="descriptionModalLabel">Edit Description</h4>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" data-bs-toggle="tooltip" title="Close"></button>
              </div>
              <div class="modal-body">
                <div class="mb-3">
                  <label for="modalDescription" class="form-label">Line Item Description</label>
                  <textarea class="form-control" id="modalDescription" rows="4" v-model="modalDescription"></textarea>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                <button type="button" class="btn btn-primary" @click="saveDescription" data-bs-dismiss="modal">Save</button>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6 col-12">
            <h4>Total Contract Value: {{ currency_with_zero(totalAmount) }}</h4>
            <h6 class="text-muted">Net: {{ currency_with_zero(netAmount) }}</h6>
            <h6 class="text-muted">Tax: {{ currency_with_zero(taxAmountTotal) }}</h6>
            <div class="mt-2">
              <small class="text-muted">
                <strong>Contract Period:</strong> {{ calculatedStartDate }} - {{ calculatedEndDate }}
              </small>
            </div>
          </div>
        </div>

        <button v-if="!isViewMode" class="btn btn-primary" @click.prevent="save" :disabled="saving">
          <img class="icon me-1" src="/img/icons/avid-icons/save.svg" alt="">Save
        </button>
        <div v-if="notice" class="alert alert-danger mt-2">{{ notice }}</div>
        <button class="btn btn-secondary ms-2" @click.prevent="back">
          <img class="icon me-1" src="/img/icons/avid-icons/close.svg" alt="">{{ isViewMode ? 'Back' : 'Cancel' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import vSelect from 'vue3-select';
import AccordionSelect from '../components/AccordionSelect.vue';
import DatepickerUtc from '../components/DatepickerUtc.vue';
import debounce from 'lodash.debounce';
import Swal from 'sweetalert2';
export default {
  name: 'ManageSubscription',
  components: { draggable, vSelect, AccordionSelect, DatepickerUtc },
  props: {
    subscriptionData: {
      type: Object,
      default: null,
    },
    initialClients: {
      type: Array,
      default: () => [],
    },
    initialAgencies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isEdit: false,
      isViewMode: false,
      saving: false,
      notice: '',
      errors: {},
      taxLoading: false,
      localTaxabilityReason: '',
      form: {
        client_id: null,
        client_url: null,
        paid_by: null,
        start_date: null,
        end_date: null,
        hubspot_deal_id: null,
        referral_agency_id: null,
        proposal_signed_at: null,
        line_items: []
      },
      clientOptions: this.initialClients,
      paidByOptions: [],
      agencyOptions: this.initialAgencies,
      dealOptions: [],
      catalogOptions: [],
      couponOptions: [],
      clientUrl: null,
      modalDescription: '',
      editingIndex: -1,
      billingContact: {address:'',city:'',region:'',postal_code:'',country:'US'},
    };
  },
  computed: {
    totalAmount() {
      return this.form.line_items.reduce((acc, li) => acc + Number(li.total_amount || 0), 0);
    },
    netAmount() {
      return this.form.line_items.reduce((acc, li) => acc + Number(li.net_amount || 0), 0);
    },
    taxAmountTotal(){ return this.form.line_items.reduce((a,li)=>a+Number(li.tax_amount||0),0); },

    paidByEntity(){
      const id = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.value : this.form.paid_by?.id;
      const type = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.type : this.form.paid_by?.type;
      if(!id||!type) return null;
      if(type==='App\\Models\\Client') return this.clientOptions.find(c=>c.id===id);
      if(type==='App\\Models\\Agency') return this.agencyOptions.find(a=>a.id===id);
      return null;
    },
    paidByEntityType(){
      if(!this.paidByEntity) return null;
      return (this.form.paid_by && typeof this.form.paid_by==='object' && this.form.paid_by.type==='App\\Models\\Agency') ? 'agency':'client';
    },
    taxFlag(){
      if(!this.paidByEntity) return false;
      return this.paidByEntityType==='client' ? !!this.paidByEntity.tax_exempt : !!this.paidByEntity.resale_certificate;
    },
    couponOptionsFiltered() {
      return this.couponOptions.filter(c => {
        // Skip coupons with null/undefined IDs
        if (!c.id) return false;

        // Show non-agency coupons
        if (!c.id.startsWith('agency_')) return true;

        // Extract values from AccordionSelect objects (consistent with save method)
        const paidById = this.form.paid_by != null && typeof this.form.paid_by === 'object' ? this.form.paid_by?.value : null;
        const paidByType = this.form.paid_by != null && typeof this.form.paid_by === 'object' ? this.form.paid_by?.type : null;
        const referralAgencyId = this.form.referral_agency_id != null && typeof this.form.referral_agency_id === 'object' ? this.form.referral_agency_id?.value : null;

        // Show agency coupon if paid_by is an agency with matching ID
        const paidByMatches = paidById &&
                              paidByType === 'App\\Models\\Agency' &&
                              c.id === `agency_${paidById}`;

        // Show agency coupon if referral agency is selected with matching ID
        const referralAgencyMatches = referralAgencyId &&
                                     c.id === `agency_${referralAgencyId}`;

        return paidByMatches || referralAgencyMatches;
      });
    },
    saveDisabled() {
      return this.saving || Object.keys(this.errors).length > 0;
    },
    // AccordionSelect formatted options
    clientOptionsFormatted() {
      return this.clientOptions.map(client => ({
        value: client.id,
        text: client.name
      }));
    },
    agencyOptionsFormatted() {
      return this.agencyOptions.map(agency => ({
        value: agency.id,
        text: agency.name
      }));
    },
    paidByOptionsFormatted() {
      // Filter paid by options based on selected client
      let filteredOptions = [];
      
      // Force reactivity by accessing the reactive property
      const clientId = this.form.client_id;
      
      if (clientId) {
        const id = typeof clientId === 'object' ? clientId?.value : clientId;
        
        // Always include the selected client first
        const selectedClient = this.clientOptions.find(c => c.id === id);
        if (selectedClient) {
          filteredOptions.push({
            value: selectedClient.id,
            text: selectedClient.name,
            type: 'App\\Models\\Client'
          });
          
          
          // Add all agencies
          this.agencyOptions.forEach(agency => {
            filteredOptions.push({
              value: agency.id,
              text: agency.name,
              type: 'App\\Models\\Agency'
            });
          });
        }
      } else {
        // If no client selected, show all options
        // Determine type by checking if option exists in agencies or clients
        filteredOptions = this.paidByOptions.map(option => {
          const isAgency = this.agencyOptions.some(a => a.id === option.id);
          const isClient = this.clientOptions.some(c => c.id === option.id);
          
          return {
            value: option.id,
            text: option.name,
            type: isAgency ? 'App\\Models\\Agency' : (isClient ? 'App\\Models\\Client' : 'App\\Models\\Client')
          };
        });
      }
      
      return filteredOptions;
    },
    dealOptionsFormatted() {
      return this.dealOptions.map(deal => ({
        value: deal.id,
        text: deal.name
      }));
    },
    catalogOptionsFormatted() {
      const options = [];
      for (let i = 0; i < this.catalogOptions.length; i++) {
        const catalog = this.catalogOptions[i];
        
        options.push({
          value: catalog.price_id,
          text: this.currency_with_zero(catalog.unit_amount/100),
          category: catalog.name,
          price: catalog.unit_amount/100,
          frequency: catalog.recurring
        });
      }
      return options;
    },
    couponOptionsFormatted() {
      // Oh honey, let's make sure couponOptionsFiltered is always an array, just like your flawless style is always on point.
      const coupons = Array.isArray(this.couponOptionsFiltered) ? this.couponOptionsFiltered : [];
      return coupons.map(coupon => ({
        value: coupon.id,
        text: coupon.name + ' (' + (coupon.percent_off ? coupon.percent_off + '%' : ('$' + coupon.amount_off/100)) + ')'
      }));
    },
    calculatedStartDate() {
      if (!this.form.start_date) return 'Not calculated';
      return this.date(this.form.start_date);
    },
    calculatedEndDate() {
      if (!this.form.end_date) return 'Not calculated';
      return this.date(this.form.end_date);
    },
    showReferralEligible() {
      return this.form.referral_agency_id;
    },
  },
  created() {
    this.paidByOptions = [...this.clientOptions, ...this.agencyOptions];
    const params = new URLSearchParams(window.location.search);
    this.isViewMode = params.get('view') === '1' || params.get('mode') === 'view';
    if (this.subscriptionData) {
        this.isEdit = true;
        this.form = {
            ...this.subscriptionData,
            paid_by: { value: this.subscriptionData.paid_by_id, text: this.subscriptionData.paid_by?.name, type: this.subscriptionData.paid_by_type },
        };
        this.clientUrl = this.subscriptionData.client?.url;
        this.billingContact = this.subscriptionData.paid_by.billing_contact || {address:'',city:'',region:'',postal_code:'',country:'US'};
        
        // Calculate term for existing line items
        this.form.line_items.forEach((li, index) => {
          if (!li.term) {
            this.recalc(index);
          }
        });
    }
    if (!this.isEdit) {
      this.clientChanged();
      // Add initial line item for new subscriptions
      this.addLineItem();
    }
  },
  mounted() { 
    // Load billing dropdowns if we have a client URL but no catalog options yet
    this.loadBillingDropdowns();
  },
  methods: {
    async loadBillingDropdowns() {
      const base = `/api/billing`;
      const dealsParams = this.isEdit && this.subscriptionData?.id ? { deal_id: this.subscriptionData.hubspot_deal_id, include_closed_won: true } : {};
      const paidById = this.form.paid_by && typeof this.form.paid_by === 'object' ? this.form.paid_by?.value : this.form.paid_by?.id;
      const [dealsRes, catalogRes, couponRes] = await Promise.all([
        window.axios.get(`${base}/hubspot/deals`, { params: dealsParams }),
        window.axios.get(`${base}/stripe/catalog`),
        window.axios.get(`${base}/stripe/coupons`, { params: { exclude_agency: true, agency_id: paidById } })
      ]);
      this.dealOptions = dealsRes.data.data;
      
      // Filter out old products and sort by name only for new subscriptions
      // Existing subscriptions can show the old products
      if (!this.isEdit) {
        const excludedProductIds = ['prod_T9iXe5u1uXZFpL', 'prod_T9iX8J9lEMMIlR', 'prod_TJajLy9knfsYDZ'];
        this.catalogOptions = catalogRes.data.data
          .filter(cat => !excludedProductIds.includes(cat.product_id))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } else {
        // For existing subscriptions, just sort by name (don't filter)
        this.catalogOptions = catalogRes.data.data
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      this.couponOptions = couponRes.data.data;

      // After loading catalog and coupon options, populate amounts for existing line items
      this.form.line_items.forEach((li, index) => {
        if (li.stripe_catalog_id) {
          this.catalogChanged(index);
        }
        if (li.stripe_coupon_id) {
          this.couponChanged(index);
        }
      });
    },
    clientChanged() {
      // Handle both direct ID and AccordionSelect object
      if (!this.form.client_id) {
        // Clear client-related data when client is removed
        this.clientUrl = null;
        this.form.client_url = null;
        return;
      }
      
      const clientId = typeof this.form.client_id === 'object' ? this.form.client_id?.value : this.form.client_id;
      const sel = this.clientOptions.find(c => c.id === clientId);
      if (sel) {
        this.clientUrl = sel.url;
        this.form.client_url = sel.url;
        
        // Only reset paid_by to the selected client when client changes in new subscriptions
        // Don't override paid_by when editing existing subscriptions
        if (!this.isEdit || !this.form.paid_by) {
          this.form.paid_by = { 
            value: sel.id, 
            text: sel.name, 
            type: 'App\\Models\\Client' 
          };
          this.paidByChanged();
        }
      }
    },
    paidByChanged() {
      const paidById = typeof this.form.paid_by === 'object' ? this.form.paid_by?.value : this.form.paid_by;
      if (!paidById) {
        this.billingContact = {id:null,address:'',city:'',region:'',postal_code:'',country:'US'};
        return;
      }
      
      // Update billing contact from the selected entity for tax calculations
      if (this.paidByEntity && this.paidByEntity.billing_contact) {
        this.billingContact = {
          id: this.paidByEntity.billing_contact.id || null,
          address: this.paidByEntity.billing_contact.address || '',
          city: this.paidByEntity.billing_contact.city || '',
          region: this.paidByEntity.billing_contact.region || '',
          postal_code: this.paidByEntity.billing_contact.postal_code || '',
          country: this.paidByEntity.billing_contact.country || 'US'
        };
      } else {
        this.billingContact = {address:'',city:'',region:'',postal_code:'',country:'US'};
      }
      
      this.queueTaxEstimate();
    },
    addLineItem() {
      if (this.isViewMode) return;
      this.form.line_items.push({
        description: '',
        name: '',
        stripe_catalog_id: null,
        unit_amount: 0,
        stripe_coupon_id: null,
        start_date: null,
        end_date: null,
        quantity: 1,
        total_amount: 0,
        net_amount: 0,
        billing_frequency: 'monthly',
        referral_eligible: false,
        order: this.form.line_items.length,
        tax_rate: 0,
        tax_amount: 0,
        stripe_product_id: null,
        term: 12, // Default term for monthly frequency
      });
      // Clear the general line items error when a new item is added
      this.errors.line_items = null;
      this.queueTaxEstimate();
    },
    removeLineItem(i) { if (this.isViewMode) return; this.form.line_items.splice(i, 1); this.queueTaxEstimate(); },
    getCatalog(priceId) {
      return this.catalogOptions.find(c => c.price_id === priceId);
    },
    getCoupon(couponId) {
      return this.couponOptions.find(c => c.id === couponId);
    },
    catalogChanged(index) {
      const li = this.form.line_items[index];
      // Handle both direct ID and AccordionSelect object
      const catalogId = typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id;
      const cat = this.getCatalog(catalogId);
      if (cat) {
        li.unit_amount = cat.unit_amount / 100;
        li.name = cat.name;
        li.description = cat.description;
        li.stripe_product_id = cat.product_id;
      } else {
        // Product was cleared - reset values
        li.unit_amount = 0;
        li.name = '';
        li.description = '';
        li.stripe_product_id = null;
        li.tax_amount = 0;
        li.tax_rate = 0;
      }
      
      // If frequency is "once", set term to 1
      if (li.billing_frequency === 'once') {
        li.term = 1;
      }
      
      this.recalc(index);
      // Clear validation error for this field
      delete this.errors[`catalog_${index}`];
    },
    couponChanged(index) { this.recalc(index); },
    frequencyChanged(index) {
      const li = this.form.line_items[index];
      // If frequency is set to "once", automatically set term to 1
      if (li.billing_frequency === 'once') {
        li.term = 1;
      }
      // Clear term validation error when frequency changes
      delete this.errors[`term_${index}`];
      this.recalc(index);
    },
    recalc(index) {
      const li = this.form.line_items[index];
      let periods = 1;
      
      // Calculate from dates if both are available, otherwise use term value
      if (li.start_date && li.end_date) {
        // Calculate term based on start/end dates and billing frequency
        const start = new Date(li.start_date);
        const end = new Date(li.end_date);
        if (end > start) {
          switch (li.billing_frequency) {
            case 'monthly':
              // Calculate months between dates for billing periods
              const startYear = start.getFullYear();
              const startMonth = start.getMonth();
              const endYear = end.getFullYear();
              const endMonth = end.getMonth();

              // Calculate months difference
              const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth);

              // Add 1 if the end date is on or after the start date in the month (partial month)
              // This rounds up to ensure any activity in a month counts as a full billing month
              const partialMonth = end.getDate() >= start.getDate() ? 1 : 0;
              periods = Math.max(1, monthsDiff + partialMonth);
              break;
            case 'quarterly':
              const quarterlyMonths = (endYear - startYear) * 12 + (endMonth - startMonth);
              periods = Math.max(1, Math.ceil(quarterlyMonths / 3));
              break;
            case 'yearly':
              periods = Math.max(1, endYear - startYear);
              break;
            case 'once':
              periods = 1;
              break;
            default:
              periods = 1;
          }
        }
        // Set the calculated term on the line item
        li.term = periods;
      } else if (li.term) {
        // Use existing term value if dates aren't available
        periods = Math.max(1, li.term);
      }
      
      li.total_amount = li.unit_amount * li.quantity * periods;
      let net = li.total_amount;
      const couponId = li.stripe_coupon_id ?
        (typeof li.stripe_coupon_id === 'object' ? li.stripe_coupon_id?.value : li.stripe_coupon_id) :
        null;
      const coupon = couponId ? this.getCoupon(couponId) : null;
      if (coupon) {
        if (coupon.percent_off) {
          net = net * (1 - coupon.percent_off / 100);
        } else if (coupon.amount_off) {
          // For recurring items, apply discount per billing period
          // For one-time items, apply discount once
          const discountAmount = coupon.amount_off / 100;
          const discountMultiplier = li.billing_frequency !== 'once' ? periods : 1;
          net = net - (discountAmount * discountMultiplier);
        }
      }
      li.net_amount = Math.max(0, net);
      
      // Clear validation errors for fields that are being updated
      delete this.errors[`term_${index}`];
      delete this.errors[`qty_${index}`];
      
      this.queueTaxEstimate();
    },
    // Debounced tax estimate trigger
    queueTaxEstimate: debounce(function(){ this.fetchTaxEstimate(); }, 500),

    async fetchTaxEstimate(){
      if(!this.form.line_items.length) return;

      const paidById = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.value : this.form.paid_by?.id;
      const paidByType = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.type : this.form.paid_by?.type;

      if(!paidById || !paidByType) return; // need payer info

      // Skip tax estimation if postal code or country is blank
      if(!this.billingContact.postal_code || !this.billingContact.country) {
        return;
      }

      // Skip tax estimation if no line items with products
      const validLineItems = this.form.line_items.filter(li => !!li.stripe_product_id);
      if(!validLineItems.length) return;

      this.taxLoading = true;

      const payload = {
        paid_by_id: paidById,
        paid_by_type: paidByType,
        billing_contact: this.billingContact,
        tax_exempt: this.taxFlag,
        line_items: this.form.line_items
          .filter(li => !!li.stripe_product_id)
          .map(li => {
            const catalogId = typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id;
            const qty = Number(li.quantity || 1);

            // Start with the original unit amount from the catalog
            let discountedUnit = Number(li.unit_amount || 0);

            // Apply coupon-based discount if one is selected
            const couponId = li.stripe_coupon_id ? (typeof li.stripe_coupon_id === 'object' ? li.stripe_coupon_id.value : li.stripe_coupon_id) : null;
            if (couponId) {
              const coupon = this.getCoupon(couponId);
              if (coupon) {
                if (coupon.percent_off) {
                  discountedUnit = discountedUnit * (1 - coupon.percent_off / 100);
                } else if (coupon.amount_off) {
                  // Flat amount is per line item, not per unit
                  const amtOff = coupon.amount_off / 100;
                  discountedUnit = Math.max(0, discountedUnit - amtOff);
                }
              }
            }

            return {
              stripe_catalog_id: catalogId,
              stripe_product_id: li.stripe_product_id,
              unit_amount: discountedUnit,
              quantity: qty,
            };
          }),
      };

      try{
        const res = await window.axios.post('/api/billing/tax-estimate', payload);
        const updated = res.data.data.line_items;
        updated.forEach((u,idx)=>{
          if(!this.form.line_items[idx]) return;
          const li = this.form.line_items[idx];
          li.tax_rate = u.tax_rate;
          // Use the calculated term for tax amount
          const periods = li.term || 1;
          li.tax_amount = u.tax_amount * li.quantity * periods;
        });
      }catch(e){ console.error('Tax estimate failed',e); }
      finally{
        this.taxLoading = false;
      }
    },
    validate() {
      this.errors = {};
      this.notice = '';
      // Handle AccordionSelect objects in validation
      const paidById = typeof this.form.paid_by === 'object' && this.form.paid_by !== null ? this.form.paid_by?.value : this.form.paid_by;

      // Validate paid_by is required
      if (!paidById) this.errors.paid_by = 'Paid by is required.';

      // client no longer required
      if (this.form.line_items.length === 0) this.errors.line_items = 'At least one line item required.';
      this.form.line_items.forEach((li,i)=>{
        const catalogId = typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id;
        if (!catalogId) this.errors[`catalog_${i}`] = 'Catalog required';
        if (!li.start_date) this.errors[`li_start_${i}`] = 'Start date required';
        if (!li.end_date) this.errors[`li_end_${i}`] = 'End date required';
        if (li.quantity<=0) this.errors[`qty_${i}`] = 'Qty>0';

        // Validate end date is after start date
        if (li.start_date && li.end_date) {
          const start = new Date(li.start_date);
          const end = new Date(li.end_date);
          if (end <= start) {
            this.errors[`li_end_${i}`] = 'End date must be after start date';
          }
        }
      });
      return Object.keys(this.errors).length===0;
    },
    async save() {
      if (this.isViewMode) return;
      if (!this.validate()) { this.notice = 'Please fix the highlighted errors.'; return; }
      this.saving = true;
      const payload = { ...this.form };

      // Handle AccordionSelect objects
      payload.client_id = this.form.client_id != null && typeof this.form.client_id === 'object' ? this.form.client_id?.value : this.form.client_id;
      payload.paid_by_id = this.form.paid_by != null && typeof this.form.paid_by === 'object' ? this.form.paid_by?.value : this.form.paid_by;
      
      // Determine paid_by_type - check object first, then fallback to checking if ID exists in agencies or clients
      let paidByType = null;
      if (this.form.paid_by != null && typeof this.form.paid_by === 'object') {
        paidByType = this.form.paid_by?.type;
      }
      
      // Fallback: if type is not set, determine it by checking if the ID exists in agencies or clients
      if (!paidByType && payload.paid_by_id) {
        const isAgency = this.agencyOptions.some(a => a.id === payload.paid_by_id);
        const isClient = this.clientOptions.some(c => c.id === payload.paid_by_id);
        
        if (isAgency) {
          paidByType = 'App\\Models\\Agency';
        } else if (isClient) {
          paidByType = 'App\\Models\\Client';
        }
      }
      
      payload.paid_by_type = paidByType;
      
      payload.hubspot_deal_id = this.form.hubspot_deal_id != null && typeof this.form.hubspot_deal_id === 'object' ? this.form.hubspot_deal_id?.value : this.form.hubspot_deal_id;
      payload.referral_agency_id = this.form.referral_agency_id != null && typeof this.form.referral_agency_id === 'object' ? this.form.referral_agency_id?.value : this.form.referral_agency_id;

      // Handle line items
      payload.line_items = this.form.line_items.map(li => ({
        ...li,
        stripe_catalog_id: typeof li.stripe_catalog_id === 'object' && li.stripe_catalog_id ? li.stripe_catalog_id?.value : li.stripe_catalog_id,
        stripe_coupon_id: typeof li.stripe_coupon_id === 'object' && li.stripe_coupon_id ? li.stripe_coupon_id?.value : li.stripe_coupon_id,
        name: li.name,
      }));

      try {
        if (this.isEdit) {
          await window.axios.put(`/api/billing/subscriptions/${this.subscriptionData.id}`, payload);
        } else {
          await window.axios.post(`/api/billing/subscriptions`, payload);
        }
        window.location.href = '/admin/subscriptions';
      } catch (error) {
        if (error.response && error.response.status === 422) {
          // Handle validation errors from server
          const validationErrors = error.response.data.errors || {};
          let errorMessages = [];

          // Collect all error messages
          Object.values(validationErrors).forEach(messages => {
            if (Array.isArray(messages)) {
              errorMessages = errorMessages.concat(messages);
            } else {
              errorMessages.push(messages);
            }
          });

          // Show SweetAlert with validation errors
          await Swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            html: errorMessages.join('<br>'),
            confirmButtonText: 'OK',
            customClass: {
                popup: 'swal-error'
            }
          });
        } else {
          // Handle other errors
          await Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'An unexpected error occurred. Please try again.',
            confirmButtonText: 'OK',
            customClass: {
              popup: 'swal-error'
            },
          });
        }
      } finally {
        this.saving = false;
      }
    },
    back() { window.history.back(); },
    openDescriptionModal(index) {
      if (this.isViewMode) return;
      this.editingIndex = index;
      this.modalDescription = this.form.line_items[index].description || '';
      const modal = new bootstrap.Modal(document.getElementById('descriptionModal'));
      modal.show();
    },
    saveDescription() {
      if (this.isViewMode) return;
      if (this.editingIndex >= 0) {
        this.form.line_items[this.editingIndex].description = this.modalDescription;
        this.editingIndex = -1;
        this.modalDescription = '';
      }
    },
    clearPaidByError() {
      this.errors.paid_by = null;
    },
    clearCatalogError(index) {
      this.errors[`catalog_${index}`] = null;
    },
    clearStartDateError(index) {
      this.errors[`li_start_${index}`] = null;
    },
    clearEndDateError(index) {
      this.errors[`li_end_${index}`] = null;
    },
    clearQuantityError(index) {
      this.errors[`qty_${index}`] = null;
    },
    termUnit(freq) {
      switch ((freq || '').toLowerCase()) {
        case 'monthly':
          return 'mos';
        case 'quarterly':
          return 'qtrs';
        case 'yearly':
          return 'yrs';
        case 'once':
          return 'time';
        default:
          return '';
      }
    },
  },
  watch: {
    'form.client_id': {
      handler(val) { 
        this.clientChanged(); 
      },
      deep: true
    },
    'form.paid_by': {
      handler(val) { 
        this.paidByChanged(); 
      },
      deep: true
    },
    'form.line_items': {
      deep: true,
      handler(newVal) {
        newVal.forEach((li, idx) => {
          this.recalc(idx);
        });
        // derive start/end dates
        const dates = newVal.flatMap(li => [li.start_date, li.end_date]).filter(Boolean).map(d => new Date(d));
        if (dates.length > 0) {
          const min = new Date(Math.min(...dates));
          const max = new Date(Math.max(...dates));
          this.form.start_date = min.toISOString().split('T')[0];
          this.form.end_date = max.toISOString().split('T')[0];
        }
      }
    },
  },
};
</script>
